import { useMemo } from 'react';
import { css } from '@emotion/react';
import { AppointmentConfirmationStatus } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { CheckListFilterSelector, FilterTraySection } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { trackingIds } from '../../tracking-ids';
import { AppointmentConfirmationStatusChip } from '../atoms/appointment-status-chips';

interface AppointmentConfirmationStatusTrayFilterProps {
  value?: AppointmentConfirmationStatus;
  onChange: (status?: AppointmentConfirmationStatus) => void;
}

/**
 * Normalizes the confirmation status value to a single value for the filter.
 * If both 'APPOINTMENT_CNF_STATUS_CONFIRMED' and 'APPOINTMENT_CNF_STATUS_UNCONFIRMED' are selected, it returns APPOINTMENT_CNF_STATUS_UNSPECIFIED.
 * If only one is selected, it returns that single value.
 * If none are selected, it returns undefined.
 * @param statuses The array of confirmation status values to normalize.
 * @returns A single confirmation status value or undefined.
 */
const normalizeConfirmationStatus = (statuses: AppointmentConfirmationStatus[]) => {
  if (statuses.length === 1) {
    return statuses[0];
  }
  if (statuses.length === 2) {
    return AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNSPECIFIED;
  }
  return undefined;
};

/**
 * Denormalizes the confirmation status value to an array of values for the filter.
 * If the status is 'APPOINTMENT_CNF_STATUS_UNSPECIFIED', it returns both 'APPOINTMENT_CNF_STATUS_CONFIRMED' and 'APPOINTMENT_CNF_STATUS_UNCONFIRMED'.
 * If the status is a single value, it returns an array with that value.
 * If the status is undefined, it returns an empty array.
 * @param status The confirmation status value to denormalize.
 * @returns An array of confirmation status values.
 */
const denormalizeConfirmationStatus = (status?: AppointmentConfirmationStatus) => {
  if (status === AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNSPECIFIED) {
    return [
      AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED,
      AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED,
    ];
  }
  return status ? [status] : [];
};

export const AppointmentConfirmationStatusTrayFilter = ({
  value,
  onChange,
}: AppointmentConfirmationStatusTrayFilterProps) => {
  const { t } = useTranslation('analytics');

  const statusOptions = useMemo(() => {
    return {
      [AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED]: (
        <AppointmentConfirmationStatusChip
          status={AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED}
          css={styles.chipStyles}
        />
      ),
      [AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED]: (
        <AppointmentConfirmationStatusChip
          status={AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED}
          css={styles.chipStyles}
        />
      ),
    };
  }, []);

  return (
    <FilterTraySection label={t('Confirmation Status')} onReset={() => onChange(undefined)} disableReset={!value}>
      <CheckListFilterSelector
        options={statusOptions}
        label={t('Confirmation Status')}
        onChange={(statuses) => {
          onChange(normalizeConfirmationStatus(statuses as AppointmentConfirmationStatus[]));
        }}
        value={denormalizeConfirmationStatus(value)}
        showLabel={false}
        trackingIdBase={`${trackingIds.filterTrayToggle}-confirmation-status`}
      />
    </FilterTraySection>
  );
};

const styles = {
  chipStyles: css`
    max-width: none;
  `,
};
